import { db } from './firebase';
import {
    doc,
    collection,
    getDoc,
    addDoc,
    updateDoc,
    deleteDoc,
    arrayUnion,
    arrayRemove,
    getDocs,
    setDoc,
    query, 
    where, 
    Timestamp
} from 'firebase/firestore';
import { normalizeCourseDates, getCourseTimeZone } from './utils/courseSchedule';

const coursesCollection = collection(db, "Courses");

const defaultArtifacts = {
    discordEnabled: true,
    googleDocsEnabled: true,
    pullRequestsEnabled: true,
    figmaEnabled: true,
    fileEnabled: true,
};


export const addCourseToFirestore = async (course, email) => {
    try {
        const docRef = await addDoc(coursesCollection, {
            name: course.name,
            description: course.description,
            startDate: course.startDate ? course.startDate.toISOString() : null,
            endDate: course.endDate ? course.endDate.toISOString() : null,
            timeZone: getCourseTimeZone(course),
            groupIds: course.groupIds || [],
            admin: [email],
            isActive: false,
            defaultTemplateId: course.defaultTemplateId ?? null,
            settings: { artifacts: defaultArtifacts, deadlineEnabled: false, editEnabled: false },
        });
        return docRef.id;
    } catch (error) {
        console.error("Error adding course: ", error);
        throw error;
    }
};

// export const fetchCoursesFromFirestore = async () => {
//     try {
//         const querySnapshot = await getDocs(coursesCollection);
//         const courses = querySnapshot.docs.map((doc) => ({
//             id: doc.id,
//             ...doc.data(),
//             startDate: doc.data().startDate ? new Date(doc.data().startDate) : null,
//         }));
//         return courses;
//     } catch (error) {
//         console.error("Error fetching courses: ", error);
//         throw error;
//     }
// };

export const fetchCoursesFromFirestore = async (email) => {
    try {
        const q = query(coursesCollection, where("admin", "array-contains", email));
        const querySnapshot = await getDocs(q);
        const courses = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...normalizeCourseDates(doc.data()),
        }));
        return courses;
    } catch (error) {
        console.error("Error fetching courses: ", error);
        throw error;
    }
};

export const updateCourseInFirestore = async (courseId, updatedCourse) => {
    try {
        const courseRef = doc(db, "Courses", courseId);
        const updatePayload = {
            name: updatedCourse.name,
            description: updatedCourse.description,
            startDate: updatedCourse.startDate ? updatedCourse.startDate.toISOString() : null,
            endDate: updatedCourse.endDate ? updatedCourse.endDate.toISOString() : null,
            timeZone: getCourseTimeZone(updatedCourse),
        };
        if (updatedCourse.defaultTemplateId !== undefined) updatePayload.defaultTemplateId = updatedCourse.defaultTemplateId;
        if (updatedCourse.settings !== undefined) updatePayload.settings = updatedCourse.settings;
        await updateDoc(courseRef, updatePayload);
    } catch (error) {
        console.error("Error updating course: ", error);
        throw error;
    }
};

export const deleteCourseFromFirestore = async (courseId) => {
    try {
        const courseRef = doc(db, "Courses", courseId);
        await deleteDoc(courseRef);
    } catch (error) {
        console.error("Error deleting course: ", error);
        throw error;
    }
};

// Fetch all groups associated with a courseId
export const fetchGroupsByCourseId = async (courseId) => {
    try {
        const courseRef = doc(db, 'Courses', courseId);
        const courseSnapshot = await getDoc(courseRef);
        if (courseSnapshot.exists()) {
            const courseData = courseSnapshot.data();
            const groupIds = courseData.groupIds || [];
            const groupPromises = groupIds.map(async (groupId) => {
                const groupRef = doc(db, 'Groups', groupId);
                const groupSnapshot = await getDoc(groupRef);
                return { id: groupId, ...groupSnapshot.data() };
            });
            const groupData = await Promise.all(groupPromises);
            return groupData;
        } else {
            console.error('Course not found');
            return [];
        }
    } catch (error) {
        console.error("Error fetching groups: ", error);
        throw error;
    }
};

export const fetchAdminsByCourseId = async (courseId) => {
    try {
        const courseRef = doc(db, 'Courses', courseId);
        const courseSnapshot = await getDoc(courseRef);
        if (courseSnapshot.exists()) {
            const courseData = courseSnapshot.data();
            const admins = courseData.admin || [];
            return admins;
        } else {
            console.error('Course not found');
            return [];
        }
    } catch (error) {
        console.error("Error fetching admins: ", error);
        throw error;
    }
};

// Add a new group
export const addGroup = async (courseId, groupName) => {
    try {
        const groupRef = await addDoc(collection(db, 'Groups'), {
            courseId,
            groupName,
            members: []
        });

        const courseRef = doc(db, 'Courses', courseId);
        await updateDoc(courseRef, {
            groupIds: arrayUnion(groupRef.id)
        });

        return groupRef.id;
    } catch (error) {
        console.error("Error adding group: ", error);
        throw error;
    }
};

export const addAdmin = async (courseId, email) => {
    try {
        const docRef = doc(coursesCollection, courseId);
        await updateDoc(docRef, {
            admin: arrayUnion(email),
        });
        console.log("Email added successfully!");
    } catch (error) {
        console.error("Error adding email: ", error);
        throw error;
    }
};


// Delete a group
export const deleteGroup = async (courseId, groupId) => {
    try {
        await deleteDoc(doc(db, 'Groups', groupId));

        const courseRef = doc(db, 'Courses', courseId);
        await updateDoc(courseRef, {
            groupIds: arrayRemove(groupId)
        });
    } catch (error) {
        console.error("Error deleting group: ", error);
        throw error;
    }
};

export const deleteAdmin = async (courseId, email) => {
    try {
        const docRef = doc(coursesCollection, courseId);
        await updateDoc(docRef, {
            admin: arrayRemove(email),
        });
        console.log("Email removed successfully!");
    } catch (error) {
        console.error("Error removing email: ", error);
        throw error;
    }
};

export const fetchGroupsByIds = async (groupIds) => {
    const groupDataPromises = groupIds.map(async (groupId) => {
        const groupRef = doc(db, "Groups", groupId); // Access specific group document by ID
        const groupDoc = await getDoc(groupRef);
        if (groupDoc.exists()) {
            return { id: groupId, ...groupDoc.data() }; // Return group data along with its ID
        } else {
            console.error("No such group:", groupId);
            return null;
        }
    });

    // Wait for all promises to resolve and filter out null results (non-existent groups)
    const groupData = await Promise.all(groupDataPromises);
    return groupData.filter(group => group !== null); // Filter out null results
};

const colorLibrary = [
    "#d9f2b4", "#bad7f2", "#fface4", "#e9d985",
    "#b5ead7", "#ff9aa2", "#c7ceea", "#f1cbff",
    "#ffdac1", "#d1c4e9", "#ffadad", "#ffd6a5",
    "#fdffb6", "#caffbf", "#9bf6ff", "#a0c4ff",
    "#bdb2ff", "#ffc6ff", "#fffffc", "#d0f4de"
];

// Random color generator
function generateRandomColor(existingColors) {
    let color;
    do {
        color = '#' + Math.floor(Math.random() * 16777215).toString(16);
    } while (existingColors.includes(color));
    return color;
}

// Generate initials from the member's name
function getInitials(name) {
    const nameParts = name.trim().split(' ');
    if (nameParts.length === 1) {
        return nameParts[0].charAt(0).toUpperCase();
    }
    return (nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)).toUpperCase();
}

export const addMemberToGroup = async (courseId, groupId, memberName, memberEmail) => {
    const userRef = doc(db, 'Users', memberEmail);
    const groupRef = doc(db, 'Groups', groupId);

    try {
        // Check if the user is already in a group for this course
        const groupsQuery = query(
            collection(db, 'Groups'),
            where('courseId', '==', courseId),
            where('members.email', '==', memberEmail)
        );
        const groupsSnapshot = await getDocs(groupsQuery);

        if (!groupsSnapshot.empty) {
            return { success: false, message: 'This user is already in a group for this course.' };
        }

        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
            // If the user does not exist, create a new user document
            await setDoc(userRef, {
                email: memberEmail,
                name: memberName,
                groupIds: [groupId],
            });
        } else {
            // If the user exists, update their group information
            await updateDoc(userRef, {
                groupIds: arrayUnion(groupId),
            });
        }

        // Get the existing colors of group members
        const groupDoc = await getDoc(groupRef);
        const existingMembers = groupDoc.data().members || [];
        const existingColors = existingMembers.map(member => member.color);

        // Find an unused color from colorLibrary
        let memberColor = colorLibrary.find(color => !existingColors.includes(color));

        // If all colors in colorLibrary are used, generate a random new color
        if (!memberColor) {
            memberColor = generateRandomColor(existingColors);
        }

        // Generate initials from memberName
        const memberInitial = getInitials(memberName);

        // Add the member's email, color, and initials to the group's members field
        await updateDoc(groupRef, {
            members: arrayUnion({
                email: memberEmail,
                name: memberName,
                color: memberColor,  // Record color in group
                initial: memberInitial,  // Record initials in group
            }),
            emails: arrayUnion(memberEmail) // Add member email to the emails field
        });

        return { success: true, message: 'Member added successfully to the group with color and initials.' };
    } catch (error) {
        console.error('Error adding member to group:', error);
        return { success: false, message: 'Failed to add member to group.' };
    }
};

export const deleteMemberFromGroup = async (courseId, groupId, memberEmail) => {
    const userRef = doc(db, 'Users', memberEmail);
    const groupRef = doc(db, 'Groups', groupId);
    try {
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
            await updateDoc(userRef, {
                groupIds: arrayRemove(groupId)
            });
        }

        const groupDoc = await getDoc(groupRef);
        const existingMembers = groupDoc.data().members || [];

        const updatedMembers = existingMembers.filter(member => member.email !== memberEmail);

        await updateDoc(groupRef, {
            members: updatedMembers,
            emails: arrayRemove(memberEmail)
        });

        return { success: true, message: 'Member removed successfully from the group and Users collection.' };
    } catch (error) {
        console.error('Error removing member from group:', error);
        return { success: false, message: 'Failed to remove member from group.' };
    }
};

export const updateIsActive = async (courseId, isActiveValue) => {
    try {
        const docRef = doc(coursesCollection, courseId);
        const updateData = { isActive: isActiveValue };
        if (isActiveValue === false) {
            const now = Timestamp.now(); // Firestore Timestamp (UTC)
            updateData.deactivatedAt = now.toDate().toISOString();
            updateData.deactivatedBy = "manual";
        }
        await updateDoc(docRef, updateData);
    } catch (error) {
        throw error;
    }
};

export const updateAllowRatingEdits = async (courseId, allowEdits) => {
    try {
        const docRef = doc(coursesCollection, courseId);
        const now = Timestamp.now();
        await updateDoc(docRef, {
            allowRatingEdits: allowEdits,
            ratingEditsUpdatedAt: now.toDate().toISOString(),
            ratingEditsUpdatedBy: "admin"
        });
        console.log(`Rating edits ${allowEdits ? 'enabled' : 'disabled'} for course ${courseId}`);
    } catch (error) {
        console.error("Error updating allowRatingEdits:", error);
        throw error;
    }
};

export const getCourseConfig = async (courseId) => {
    try {
        const docRef = doc(db, "Courses", courseId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return docSnap.data();
        }
        return null;
    } catch (error) {
        console.error("Error fetching course config:", error);
        throw error;
    }
};


export const fetchCoursesByEmailFromFirestore = async (email) => {
    try {
        const userRef = doc(db, 'Users', email);
        const userDoc = await getDoc(userRef);

        if (!userDoc.exists()) {
            throw new Error("User not found");
        }

        const { groupIds } = userDoc.data();

        if (!groupIds || groupIds.length === 0) {
            return [];
        }

        const groupRefs = groupIds.map((groupId) => doc(db, 'Groups', groupId));
        const groupDocs = await Promise.all(groupRefs.map((groupRef) => getDoc(groupRef)));

        const groupsAndCourses = groupDocs
            .filter((groupDoc) => groupDoc.exists())
            .map((groupDoc) => ({
                groupId: groupDoc.id,
                courseId: groupDoc.data().courseId
            }))
            .filter((item) => item.courseId);

        if (groupsAndCourses.length === 0) {
            return [];
        }

        const coursePromises = groupsAndCourses.map(async (item) => {
            const courseRef = doc(db, 'Courses', item.courseId);
            const courseDoc = await getDoc(courseRef);

            if (courseDoc.exists() && courseDoc.data().isActive && courseDoc.data().isActive === true) {
                return {
                    courseId: courseDoc.id,
                    groupId: item.groupId,
                    ...normalizeCourseDates(courseDoc.data()),
                };
            }
            return null;
        });

        const courses = await Promise.all(coursePromises);

        return courses.filter((course) => course !== null);
    } catch (error) {
        console.error("Error fetching courses: ", error);
        throw error;
    }
};

// ---------- Course settings & emails_template subcollection ----------


/** Get course document including defaultTemplateId and settings (with defaults for missing). */
export const getCourseSettings = async (courseId) => {
    try {
        const courseRef = doc(db, "Courses", courseId);
        const snap = await getDoc(courseRef);
        if (!snap.exists()) return null;
        const data = snap.data();
        const settings = data.settings ?? {};
        const artifacts = {
            ...defaultArtifacts,              
            ...(settings.artifacts || {}),
        };
        return {
            ...normalizeCourseDates(data),
            id: snap.id,
            defaultTemplateId: data.defaultTemplateId ?? null,
            settings: { ...settings, artifacts },
        };
    } catch (error) {
        console.error("Error getting course settings: ", error);
        throw error;
    }
};

/** Update only defaultTemplateId and/or settings (merge with existing so we never wipe other keys like isActive). */
export const updateCourseSettings = async (courseId, partialSettings) => {
    try {
        const courseRef = doc(db, "Courses", courseId);
        const updatePayload = {};
        if (partialSettings.defaultTemplateId !== undefined) updatePayload.defaultTemplateId = partialSettings.defaultTemplateId;
        if (partialSettings.settings !== undefined) {
            const snap = await getDoc(courseRef);
            const existingSettings = snap.exists() ? (snap.data().settings || {}) : {};
            updatePayload.settings = { ...existingSettings, ...partialSettings.settings };
        }
        if (Object.keys(updatePayload).length === 0) return;
        await updateDoc(courseRef, updatePayload);
    } catch (error) {
        console.error("Error updating course settings: ", error);
        throw error;
    }
};

const courseEmailTemplatesCollection = (courseId) => collection(db, "Courses", courseId, "emails_template");

/** List all email templates for a course. */
export const listCourseEmailTemplates = async (courseId) => {
    try {
        const col = courseEmailTemplatesCollection(courseId);
        const snapshot = await getDocs(col);
        return snapshot.docs.map((d) => ({ 
            id: d.id, 
            ...d.data(), 
            createdAt: d.data().createdAt?.toDate?.() ?? d.data().createdAt, 
            updatedAt: d.data().updatedAt?.toDate?.() ?? d.data().updatedAt 
        }));
    } catch (error) {
        console.error("Error listing course email templates: ", error);
        throw error;
    }
};

/** Create a course email template (sourceTemplateId null for own template). */
export const createCourseEmailTemplate = async (courseId, template) => {
    try {
        const col = courseEmailTemplatesCollection(courseId);
        const now = Timestamp.now();
        const docRef = await addDoc(col, {
            title: template.title ?? "",
            subject: template.subject ?? "",
            body: template.body ?? "",
            sourceTemplateId: template.sourceTemplateId ?? null,
            updateBy: template.updateBy ?? "",
            createdAt: now,
            updatedAt: now,
        });
        return docRef.id;
    } catch (error) {
        console.error("Error creating course email template: ", error);
        throw error;
    }
};

/** Update a course email template. */
export const updateCourseEmailTemplate = async (courseId, templateId, updates) => {
    try {
        const templateRef = doc(db, "Courses", courseId, "emails_template", templateId);
        const now = Timestamp.now();
        const payload = { updatedAt: now };
        if (updates.title !== undefined) payload.title = updates.title;
        if (updates.subject !== undefined) payload.subject = updates.subject;
        if (updates.body !== undefined) payload.body = updates.body;
        if (updates.updateBy !== undefined) payload.updateBy = updates.updateBy;
        await updateDoc(templateRef, payload);
    } catch (error) {
        console.error("Error updating course email template: ", error);
        throw error;
    }
};

/** Delete a course email template. */
export const deleteCourseEmailTemplate = async (courseId, templateId) => {
    try {
        const templateRef = doc(db, "Courses", courseId, "emails_template", templateId);
        await deleteDoc(templateRef);
    } catch (error) {
        console.error("Error deleting course email template: ", error);
        throw error;
    }
};

/** Get a single course email template by id. */
export const getCourseEmailTemplate = async (courseId, templateId) => {
    try {
        const templateRef = doc(db, "Courses", courseId, "emails_template", templateId);
        const snap = await getDoc(templateRef);
        if (!snap.exists()) return null;
        const data = snap.data();
        return { id: snap.id, ...data, createdAt: data.createdAt?.toDate?.() ?? data.createdAt, updatedAt: data.updatedAt?.toDate?.() ?? data.updatedAt };
    } catch (error) {
        console.error("Error getting course email template: ", error);
        throw error;
    }
};

/** Copy a global EmailTemplates doc into the course's emails_template subcollection. Returns new course template id. */
export const copyGlobalTemplateToCourse = async (courseId, globalTemplateId, updateBy) => {
    const global = await getEmailTemplate(globalTemplateId);
    if (!global) throw new Error("Global template not found");
    return createCourseEmailTemplate(courseId, {
        title: global.title,
        subject: global.subject,
        body: global.body,
        sourceTemplateId: globalTemplateId,
        updateBy,
    });
};

/** Upload a course email template to the global EmailTemplates collection. Returns new global template id. description is required. contentOverrides optional { title, subject, body } for editing before share. */
export const uploadCourseTemplateToGlobal = async (courseId, courseTemplateId, createdByUser, description, contentOverrides = null) => {
    const desc = typeof description === "string" ? description.trim() : "";
    if (!desc) throw new Error("Description is required when sharing to public templates.");
    const courseTemplate = await getCourseEmailTemplate(courseId, courseTemplateId);
    if (!courseTemplate) throw new Error("Course template not found");
    const title = contentOverrides?.title ?? courseTemplate.title;
    const subject = contentOverrides?.subject ?? courseTemplate.subject;
    const body = contentOverrides?.body ?? courseTemplate.body;
    return createEmailTemplate({
        title,
        description: desc,
        subject,
        body,
        createdByCourse: courseId,
        createdByUser,
    });
};

// ---------- Global EmailTemplates collection ----------
const emailTemplatesCollection = collection(db, "EmailTemplates");

/** List all global email templates. */
export const listEmailTemplates = async () => {
    try {
        const snapshot = await getDocs(emailTemplatesCollection);
        return snapshot.docs.map((d) => {
            const data = d.data();
            return { id: d.id, ...data, createdAt: data.createdAt?.toDate?.() ?? data.createdAt };
        });
    } catch (error) {
        console.error("Error listing email templates: ", error);
        throw error;
    }
};

/** Get a single global email template by id. */
export const getEmailTemplate = async (templateId) => {
    try {
        const templateRef = doc(db, "EmailTemplates", templateId);
        const snap = await getDoc(templateRef);
        if (!snap.exists()) return null;
        const data = snap.data();
        return { id: snap.id, ...data, createdAt: data.createdAt?.toDate?.() ?? data.createdAt };
    } catch (error) {
        console.error("Error getting email template: ", error);
        throw error;
    }
};

/** Create a global email template. */
export const createEmailTemplate = async (template) => {
    try {
        const now = Timestamp.now();
        const docRef = await addDoc(emailTemplatesCollection, {
            title: template.title ?? "",
            description: template.description ?? "",
            subject: template.subject ?? "",
            body: template.body ?? "",
            createdByCourse: template.createdByCourse ?? "",
            createdByUser: template.createdByUser ?? "",
            createdAt: now,
        });
        return docRef.id;
    } catch (error) {
        console.error("Error creating email template: ", error);
        throw error;
    }
};

/** Update a global email template. */
export const updateEmailTemplate = async (templateId, updates) => {
    try {
        const templateRef = doc(db, "EmailTemplates", templateId);
        const payload = {};
        if (updates.title !== undefined) payload.title = updates.title;
        if (updates.description !== undefined) payload.description = updates.description;
        if (updates.subject !== undefined) payload.subject = updates.subject;
        if (updates.body !== undefined) payload.body = updates.body;
        if (Object.keys(payload).length === 0) return;
        await updateDoc(templateRef, payload);
    } catch (error) {
        console.error("Error updating email template: ", error);
        throw error;
    }
};
